/**
 * Pure helpers for PWA install eligibility, dismissal cooldown, and platform detection.
 * localStorage is used only for a versioned dismiss timestamp (no PII / auth).
 */

export const PWA_DISMISS_STORAGE_KEY = 'riestro:pwa-install-dismissed-until:v1';
export const PWA_DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;
export const PWA_PROMOTE_DELAY_MS = 2000;

export const PWA_COPY = {
  title: 'Встановіть Рієстро',
  body: 'Додайте Рієстро на головний екран для швидкого доступу та роботи у форматі застосунку.',
  install: 'Встановити',
  howToInstall: 'Як встановити',
  notNow: 'Не зараз',
  close: 'Закрити',
  instructionsTitle: 'Як встановити Рієстро',
  instructions: [
    'Натисніть “Поділитися” або відкрийте меню браузера.',
    'Оберіть “На початковий екран”.',
    'Натисніть “Додати”.',
  ],
  androidManual:
    'Відкрийте меню браузера та оберіть “Встановити застосунок” або “Додати на головний екран”.',
};

export function isStandaloneDisplayMode(win = typeof window !== 'undefined' ? window : null) {
  if (!win) return false;
  try {
    if (typeof win.matchMedia === 'function') {
      if (win.matchMedia('(display-mode: standalone)').matches) return true;
    }
  } catch (_err) {
    /* ignore */
  }
  try {
    if (win.navigator && win.navigator.standalone === true) return true;
  } catch (_err) {
    /* ignore */
  }
  return false;
}

export function isIosLike(nav = typeof navigator !== 'undefined' ? navigator : null) {
  if (!nav) return false;
  const ua = nav.userAgent || '';
  if (/iPhone|iPod|iPad/i.test(ua)) return true;
  // iPadOS 13+ may report as MacIntel while exposing multi-touch.
  const platform = nav.platform || '';
  const maxTouchPoints = typeof nav.maxTouchPoints === 'number' ? nav.maxTouchPoints : 0;
  if (platform === 'MacIntel' && maxTouchPoints > 1) return true;
  return false;
}

export function isMobileOrTabletContext(win = typeof window !== 'undefined' ? window : null) {
  if (!win || !win.navigator) return false;
  const nav = win.navigator;
  const ua = nav.userAgent || '';

  if (isIosLike(nav)) return true;
  if (/Android|webOS|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua)) return true;

  // Coarse pointer / hover-none is a reasonable tablet/phone signal without trusting UA alone.
  try {
    if (typeof win.matchMedia === 'function') {
      if (win.matchMedia('(pointer: coarse)').matches && win.matchMedia('(hover: none)').matches) {
        return true;
      }
      if (win.matchMedia('(max-width: 1024px) and (pointer: coarse)').matches) {
        return true;
      }
    }
  } catch (_err) {
    /* ignore */
  }

  return false;
}

export function readDismissedUntil(storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(PWA_DISMISS_STORAGE_KEY);
    if (raw == null || raw === '') return null;
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return null;
    return value;
  } catch (_err) {
    return null;
  }
}

export function isDismissCooldownActive(
  now = Date.now(),
  storage = typeof localStorage !== 'undefined' ? localStorage : null
) {
  const until = readDismissedUntil(storage);
  if (until == null) return false;
  return until > now;
}

export function writeDismissCooldown(
  now = Date.now(),
  storage = typeof localStorage !== 'undefined' ? localStorage : null
) {
  if (!storage) return false;
  try {
    storage.setItem(PWA_DISMISS_STORAGE_KEY, String(now + PWA_DISMISS_COOLDOWN_MS));
    return true;
  } catch (_err) {
    return false;
  }
}

export function clearDismissCooldown(storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  if (!storage) return false;
  try {
    storage.removeItem(PWA_DISMISS_STORAGE_KEY);
    return true;
  } catch (_err) {
    return false;
  }
}

/**
 * Decide which install path is available.
 * @returns {'chromium'|'ios'|'android-manual'|null}
 */
export function resolveInstallPath({
  hasDeferredPrompt = false,
  iosLike = false,
  mobile = false,
  androidUa = false,
} = {}) {
  if (!mobile) return null;
  if (iosLike) return 'ios';
  if (hasDeferredPrompt) return 'chromium';
  if (androidUa) return 'android-manual';
  return null;
}

export function isAndroidUserAgent(nav = typeof navigator !== 'undefined' ? navigator : null) {
  if (!nav) return false;
  return /Android/i.test(nav.userAgent || '');
}
